<template>
  <div>
    <h4
      v-if="form.title"
      class="mb-2 pl-2"
    >
      {{ form.title }}
    </h4>

    <p
      v-if="form.description"
      class="mt-0 pl-2"
    >
      {{ form.description }}
    </p>

    <div
      v-for="field in form.fields"
      class="p-0 float-left w-full"
      :key="field.name"
    >
      <div
        v-show="field.show && field.showFromBack && field.name !== 'internal_note'"
        class="p-1 my-2"
      >

        <p class="my-1 font-medium">
          {{ field.label }}
          <span v-if="!field.optional">
            *
          </span>
        </p>

        <!--text/email-->
        <input
          :id="field.name"
          v-if="field.type === 'text' || field.type === 'date'"
          v-model="field.value"
          class="w-full my-1 py-1 px-3 min-h-12 border-gray-200 rounded focus:border-orange-organization focus:ring-orange-organization disabled:bg-gray-100 transition-colors"
          :name="field.name"
          :disabled="field.disabled"
          :type="field.type"
        />

        <!--textarea-->
        <textarea
          :id="field.name"
          v-if="field.type === 'textarea'"
          v-model="field.value"
          class="w-full my-1 py-1 px-3 min-h-12 border-gray-200 rounded focus:border-orange-organization focus:ring-orange-organization disabled:bg-gray-100 transition-colors"
          :name="field.name"
          :disabled="field.disabled"
        />

        <!--select-->
        <vSelect
          :id="field.name"
          v-if="field.type === 'select'"
          v-model="field.value"
          :name="field.name"
          :options="field.options"
          :disabled="field.disabled"
          :reduce="option => option.value"
          label="text"
          code="value"
          class="w-full my-1 min-h-12 border-gray-200 rounded focus:border-orange-organization focus:ring-orange-organization active:ring-orange-organization active:border-orange-organization disabled:bg-gray-100 transition-colors"
          @input="findConditionsAndModifyShowProps($event, field.name)"
        />

        <!--radio-->
        <div
          v-if="field.type === 'radio'"
          :id="`radio-button-container-${field.name}`"
          class="w-full my-1 flex flex-col justify-start items-stretch pt-2 space-y-5 sm:space-y-2"
        >
          <div
            v-for="(option, index) in field.options"
            :key="`option-${field.name}-${index}`"
            :id="`container-option-${field.name}-${index}`"
            class="flex flex-row justify-start items-center"
          >
            <input
              :id="`option-${field.name}-${index}`"
              v-model="field.value"
              :disabled="field.disabled"
              type="radio"
              :value="option.value"
              class="text-orange-organization focus:ring-0 active:ring-0 border-gray-500 disabled:border-gray-200 disabled:bg-gray-100"
              @change="findConditionsAndModifyShowProps($event.target.value, field.name)"
            />
              <label
                :for="`option-${field.name}-${index}`"
                class="ml-2 text-gray-800 leading-5"
              >
                {{ option.text }}
              </label>
          </div>
        </div>

        <!--checkbox-->
        <div
          v-if="field.type === 'checkbox'"
          :id="`checkbox-button-container-${field.name}`"
          class="w-full my-1 flex flex-col justify-start items-stretch pt-2 space-y-5 sm:space-y-2"
        >
          <div
            v-for="(option, index) in field.options"
            :key="`option-${field.name}-${index}`"
            :id="`container-option-${field.name}-${index}`"
            class="flex flex-row justify-start items-center"
          >
            <input
              :id="`option-${field.name}-${index}`"
              v-model="field.value"
              :disabled="field.disabled"
              type="checkbox"
              :value="option.value"
              class="text-orange-organization focus:ring-0 active:ring-0 rounded-sm border-gray-500 disabled:border-gray-200 disabled:bg-gray-100"
              @change="findConditionsAndModifyShowProps(field.value, field.name)"
            />
              <label
                :for="`option-${field.name}-${index}`"
                class="ml-2 text-gray-800 leading-5"
              >
                {{ option.text }}
              </label>
          </div>
        </div>

        <!--function-->
        <div
          v-if="field.type === 'function'"
          class="my-1"
        >
          <p class="text-gray-800">{{ field.value }}</p>
        </div>

        <!--file-->
        <div
          :id="`upload-main-container-${field.name}`"
          v-if="field.type === 'file'"
          class="w-full flex flex-col justify-start items-stretch pt-2.5 border-t border-gray-200"
        >
          <div
            :id="`upload-header-${field.name}`"
            class="w-full"
          >
            <button
              :id="`attach-button-${field.name}`"
              class="w-full sm:w-auto flex flex-row justify-center items-center py-3 sm:py-2 px-6 rounded shadow text-white bg-gray-600 hover:bg-gray-500 active:bg-gray-700 disabled:bg-gray-500 disabled:text-gray-200 disabled:cursor-default transition-colors"
              @click="openExplorer(field.name)"
              :disabled="uploading || field.disabled"
            >
              <svg
                v-if="!uploading"
                xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                />
              </svg>
              <svg
                v-if="uploading"
                class="w-4 h-4 animate-spin"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                />
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
              </svg>
              <span class="ml-2">
                {{ uploading ? "Anexando arquivos..." : "Anexar arquivos" }}
              </span>
            </button>

            <p class="mt-1 text-xs text-gray-500">
              {{ field | formatFileRules }}
            </p>

            <input
              type="file"
              class="hidden"
              :id="field.name"
              :ref="field.name"
              multiple
              @change="onChangeFileUpload(field)"
            />
          </div>

          <div
            v-if="field.value && field.value.uploads"
            :id="`uploads-list-${field.name}`"
            class="w-full flex flex-row flex-wrap pt-4 justify-center items-center mb-2.5"
          >
            <div
              v-for="(upload, key) in field.value.uploads"
              :key="`upload-item-${field.name}-${key}`"
              :id="`upload-item-${field.name}-${key}`"
              class="min-w-50 w-full sm:w-5/12 min-h-36 max-h-36 relative flex flex-col justify-start items-stretch mx-2 my-1 border border-gray-200 rounded-sm"
            >
              <div
                :id="`upload-item-header-${field.name}-${key}`"
                class="mt-1 text-right">
                <button
                  class="p-1 mr-1 rounded-full text-gray-400 hover:text-gray-500 hover:bg-gray-100 active:text-gray-600 active:bg-gray-200 transition-colors"
                  @click="checkDelete(field.name, upload._id)"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div
                :id="`upload-item-content-${field.name}-${key}`"
                class="w-full flex flex-col justify-start items-stretch text-center -mt-3.5 h-24 px-4"
              >
                <div class="flex justify-center w-full">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="w-12 h-12 text-gray-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                <p
                  class="text-gray-600 font-medium text-center leading-5 mt-1 line-clamp-2"
                  :title="upload.filename | formatFileName"
                >
                  {{ upload.filename | formatFileName }}
                </p>
              </div>

              <button
                :id="`upload-item-button-${field.name}-${key}`"
                class="w-full absolute bottom-0 bg-gray-300 py-2 mt-2 uppercase text-gray-600 font-medium hover:bg-gray-200 active:bg-gray-400 transition-colors disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-default"
                :disabled="downloading === upload._id"
                @click="downloadFile(upload._id)"
              >
                {{ downloading === upload._id ? 'Fazendo download...' : 'Download' }}
              </button>
            </div>
          </div>

          <ExternalModal :show="activeDelete">
            <div slot="content">
              <div class="px-4 pt-5 pb-4 bg-white">
                <div class="flex items-start">
                  <div class="flex items-center justify-center mx-0 bg-red-100 rounded-full w-11 h-11">
                    <svg
                      class="w-6 h-6 text-orange-organization"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                  </div>
                  <div class="m-2 text-center">
                    <h3 class="text-lg font-medium text-gray-800">
                      Excluir arquivo?
                    </h3>
                  </div>
                </div>
              </div>
              <div class="flex justify-end px-4 py-3 space-x-1 bg-gray-50">
                <button
                  type="button"
                  :disabled="deleting"
                  class="flex justify-center w-3/12 px-2 py-1 font-medium text-black transition-opacity duration-300 bg-white border border-gray-200 rounded disabled:cursor-not-allowed focus:outline-none focus:ring-0 disabled:opacity-50 hover:opacity-80"
                  @click="activeDelete = false, selectedFile = null"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  class="flex items-center justify-center w-3/12 px-2 py-1 font-medium text-white transition-opacity duration-300 border border-transparent rounded disabled:cursor-not-allowed focus:outline-none focus:ring-0 disabled:opacity-50 bg-orange-organization hover:opacity-80"
                  :disabled="deleting"
                  @click="deleteFile"
                >
                  <svg
                    v-if="deleting"
                    class="w-5 h-5 mr-1 -ml-1 text-white animate-spin"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    />
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  {{ deleting ? 'Excluindo...' : 'Excluir' }}
                </button>
              </div>
            </div>
          </ExternalModal>
        </div>
      </div>
    </div>

    <ExternalSnackbar
      :show="snackBar.show"
      @closed="snackBar.show = false"
    >
      {{ snackBar.message }}
    </ExternalSnackbar>
  </div>
</template>

<script>
import ExternalSnackbar from '@/components/commons/ExternalSnackbar'
import ExternalModal from '@/components/commons/ExternalModal'
import EventBus from '@/utils/event-bus'
import {
  create as UploadCreate,
  deleteOne as UploadDeleteOne,
  findOne as UploadFindOne,
  createPresignedPost as UploadCreatePresignedPost
} from '@/repositories/integrations/mobly/services/csExternal/upload'

export default {
  name: 'ExternalDynamicForm',
  components: {
    ExternalSnackbar,
    ExternalModal
  },
  props: {
    form: {
      type: Object,
      required: true
    },
    type: {
      type: String,
      required: false,
      default: 'default'
    },
    organization: {
      type: Object,
      required: true
    }
  },

  data () {
    return {
      keys: [],
      webserviceFields: [
        'requestHeaders',
        'requestData',
        'responseHeaders',
        'responseData'
      ],
      snackBar: {
        show: false,
        message: ''
      },
      uploading: false,
      downloading: false,
      deleting: false,
      activeDelete: false,
      selectedFile: null,
      selectedField: null,
      files: []
    }
  },

  mounted () {
    this.format()
    this.initFormFields()
  },

  filters: {
    formatFileName: value => (value.split('] ')[1]),
    formatFileRules: field => {
      const extensions = field.fileExtensions
      const message = `* Extensões válidas: ${extensions.join('/')}
       | Número de arquivos: mínimo ${field.minFileNumber}
       e máximo ${field.maxFileNumber} |
       Tamanho máximo do arquivo: ${field.fileSize}Mb`
      return message
    }
  },

  methods: {
    initFormFields () {
      this.form.fields.forEach(field => {
        if (field.value) this.findConditionsAndModifyShowProps(field.value, field.name)
        if (!field.showFromBack) return
        if (!field.conditions || !field.conditions[0]) return
        field.conditions.forEach(condition => {
          if (!condition.blocks || !condition.blocks[0]) return
          if (condition.blocks.every(item => item.type === 'ticket' && item.checkedTicket)) {
            field.show = true
          }
        })
      })
    },

    format () {
      for (let i = 0; i < this.form.fields.length; i++) {
        // Se as options estiverem como string, faz o parse.
        // Vem como string quando o value é dinâmico e passa pelo placeholder
        if (this.form.fields[i].options && typeof this.form.fields[i].options === 'string') {
          try {
            const options = []
            const parsedOptions = this.form.fields[i].options = JSON.parse(this.form.fields[i].options)

            for (const [key, value] of Object.entries(parsedOptions)) {
              options.push({ value: key, text: value })
            }

            this.form.fields[i].options = options
          } catch (e) {
            this.form.fields[i].options = [{
              value: 'invalid_placeholder',
              text: `[Placeholder Inválido] ${this.form.fields[i].options}`
            }]
          }
        }

        if (
          !this.form.fields[i].value ||
          !this.webserviceFields.includes(this.form.fields[i].name) ||
          typeof this.form.fields[i].value !== 'object'
        ) {
          for (const condition of this.form.fields[i].conditions) {
            // Pega as keys na estrutura nova
            if ('blocks' in condition) {
              for (const block of condition.blocks) {
                if ('key' in block) {
                  this.keys.push(block.key)
                }
              }
            } else {
              // Pega as keys na estrutura antiga
              if ('key' in condition) {
                this.keys.push(condition.key)
              }
            }
          }

          continue
        }
        this.form.fields[i].value = JSON.stringify(
          this.form.fields[i].value,
          null,
          2
        )
      }
    },

    findAndModifyChildsByParent (key) {
      const typesToIgnore = ['function']

      if (!(key in this.form.fieldStateControl)) return

      for (let i = this.form.fieldStateControl[key].length - 1; i >= 0; i--) {
        this.findAndModifyChildsByParent(
          this.form.fieldStateControl[key][i > 1 ? i - 1 : i]
        )

        this.form.fields.forEach(field => {
          if (field.name !== this.form.fieldStateControl[key][i]) return

          if (!typesToIgnore.includes(field.type) && field.show && field.showFromBack) {
            field.value = field.type === 'checkbox' ? [] : null
          }

          field.show = false

          this.form.fieldStateControl[key].splice(i, 1)
        }, this)
      }
    },

    compareValues (blockValue, fieldValue, operator) {
      if (fieldValue instanceof Array === false) {
        if (operator === '==') return blockValue === fieldValue
        if (operator === '!=') return blockValue !== fieldValue
      }

      const results = []

      fieldValue.forEach(value => {
        if (operator === '==') results.push(blockValue === value)
        if (operator === '!=') results.push(blockValue !== value)
      })

      if (operator === '==') {
        return results.includes(true)
      }

      if (operator === '!=') {
        return results.every(value => value === true)
      }
    },

    findConditionsAndModifyShowProps (event, name) {
      if (!('fieldStateControl' in this.form)) {
        this.form.fieldStateControl = {}
      }

      if (event === null) return

      this.findAndModifyChildsByParent(name)

      this.form.fields.forEach((field) => {
        if (field.showFromBack === false) return

        field.conditions.forEach((condition, conditionIndex, conditionsArray) => {
          if ('blocks' in condition) {
            // Se field.showFromBack === null, significa que existe um block de condition que é só do tipo form
            let formTypeOnly = true

            condition.blocks.forEach((block, blockIndex, blocksArray) => {
              if (block.type !== 'form') return
              if (block.name !== name) return
              if (!this.compareValues(block.value, event, block.operator)) return

              // Se chegou até aqui, TALVEZ deva ser exibido o field!
              const showField = []

              blocksArray.forEach(blockItem => {
                if (blockItem.type === 'ticket' && this.keys.includes(blockItem.key)) {
                  formTypeOnly = false
                  const checkedTicket = Object.hasOwn(blockItem, 'checkedTicket') ? blockItem.checkedTicket : true
                  showField.push(checkedTicket)
                  return
                }

                this.form.fields.forEach(field => {
                  if (field.name !== blockItem.name) return
                  if (!this.compareValues(blockItem.value, field.value, blockItem.operator)) return
                  showField.push(true)
                })
              })

              if (field.showFromBack === null &&
                formTypeOnly &&
                (showField.length === blocksArray.length) &&
                showField.every(item => item === true)) {
                field.showFromBack = true
                field.show = true
              }

              if (showField.length === blocksArray.length &&
                showField.every(item => item === true)) {
                field.show = true
              }

              if (!(name in this.form.fieldStateControl)) {
                this.form.fieldStateControl[name] = []
              }

              if (this.form.fieldStateControl[name].includes(field.name)) return

              this.form.fieldStateControl[name].push(field.name)
            })
          } else {
            const eventIsArray = event instanceof Array

            if (condition.type !== 'form') return
            if (condition.name !== name) return
            if (!eventIsArray && condition.value !== event) return
            if (eventIsArray && !event.includes(condition.value)) return

            const showField = []

            conditionsArray.forEach(conditionItem => {
              if (conditionItem.type === 'ticket' && this.keys.includes(conditionItem.key)) {
                showField.push(true)
              }
              this.form.fields.forEach(field => {
                const fieldIsArray = field.value instanceof Array
                if (field.name !== conditionItem.name) return
                if (!fieldIsArray && field.value !== conditionItem.value) return
                if (fieldIsArray && !field.value.includes(conditionItem.value)) return
                showField.push(true)
              })
            })

            if (showField.length === conditionsArray.length &&
              showField.every(item => item === true)) {
              field.show = true
            }

            if (!(name in this.form.fieldStateControl)) {
              this.form.fieldStateControl = {
                [name]: [field.name],
                ...this.form.fieldStateControl
              }
              return
            }

            if (this.form.fieldStateControl[name].includes(field.name)) return

            this.form.fieldStateControl[name].push(field.name)
          }
        })
      })
    },

    openExplorer (fieldName) {
      this.$refs[fieldName][0].click()
    },

    onChangeFileUpload (field) {
      this.files[field.name] = []

      try {
        this.$refs[field.name][0].files.forEach(file => {
          const splitedFileName = file.name.split('.')
          const fileExtension = splitedFileName[splitedFileName.length - 1]

          // Verify extensions
          if (!field.fileExtensions.includes(fileExtension.toLowerCase())) {
            this.files[field.name] = []
            throw new Error('Arquivo com extensão inválida.')
          }

          // Verify size
          if (file.size > field.fileSize * 1048576) {
            this.files[field.name] = []
            throw new Error('Arquivo com tamanho inválido.')
          }

          const fieldUploadsLength = field.value && field.value.uploads ? field.value.uploads.length : 0
          if ((this.files[field.name].length + fieldUploadsLength) < field.maxFileNumber) {
            this.files[field.name].push(file)
          } else {
            throw new Error(`Número de arquivos: máximo ${field.maxFileNumber}`)
          }
        })

        this.submitForm(field.name)
      } catch (e) {
        console.log(1)
        this.snackBar.show = true
        this.snackBar.message = e.message
      }
    },

    async submitForm (fieldName) {
      this.uploading = true

      try {
        await Promise.all(
          this.files[fieldName].map(async (file, index) => {
            // Cria a URL para fazer upload no S3
            const presignedResponse = await UploadCreatePresignedPost({
              fileName: file.name,
              fileType: file.type,
              fileSize: file.size
            }, this.organization)

            const formData = new FormData()

            formData.append('key', presignedResponse.data.key)

            Object.entries(presignedResponse.data.fields).forEach(([k, v]) => {
              formData.append(k, v)
            })

            formData.append('file', file)

            // Faz o Upload do arquivo direto para o S3
            await fetch(presignedResponse.data.url, {
              method: 'POST',
              body: formData
            })

            const uploadFormData = new FormData()

            uploadFormData.append('createdByPresignedUrl', true)
            uploadFormData.append('path', presignedResponse.data.path)
            uploadFormData.append('filename', presignedResponse.data.filename)

            // Cria o Upload no banco
            const response = await UploadCreate(uploadFormData, this.organization)

            const fileData = {
              _id: response.data._id,
              filename: response.data.filename
            }

            const field = this.form.fields.find(
              field => field.name === fieldName
            )

            if (!field.value) field.value = { uploads: [] }

            field.value.uploads.push(fileData)
          })
        )
        EventBus.$emit('saveForm', false)
      } catch (e) {
        this.snackBar.show = true
        this.snackBar.message = 'Não foi possível anexar os arquivos, tente novamente.'
      }

      this.uploading = false
    },

    checkDelete (fieldName, id) {
      this.selectedField = fieldName
      this.selectedFile = id
      this.activeDelete = true
    },

    async deleteFile () {
      this.deleting = true

      try {
        await UploadDeleteOne(this.selectedFile, this.organization)

        const field = this.form.fields.find(
          field => field.name === this.selectedField
        )

        field.value.uploads = field.value.uploads.filter(
          upload => upload._id !== this.selectedFile
        )

        EventBus.$emit('saveForm', false)
      } catch (e) {
        this.snackBar.show = true
        this.snackBar.message =
          e.message || 'Não foi possível deletar o arquivo, tente novamente.'
      }

      this.selectedFile = null
      this.selectedFile = null
      this.activeDelete = false
      this.deleting = false
    },

    async downloadFile (id) {
      this.downloading = id
      const uploadParams = 'fields=url'
      const upload = await UploadFindOne(id, uploadParams, this.organization)
      const element = document.createElement('a')
      element.setAttribute('href', upload.data.url)
      element.click()
      setTimeout(() => {
        this.downloading = null
      }, 1000)
    }
  }
}
</script>
